import React, { ComponentProps, memo } from "react";
import ReactMarkdown, { Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import { ErrorBoundary } from "react-error-boundary";
import { BaseMessage } from "../types";
import { Spinner } from "@viewComponents/campaign_builder_dashboard/components/spinner";
import "highlight.js/styles/github-dark.css";

interface SynthesizerMessageProps {
  message: BaseMessage;
}

// Minimal markdown fixes for common LLM formatting issues
const fixMarkdown = (text: string): string => {
  if (!text) return text;

  return (
    text
      // Fix tables with missing blank line before them
      // Uses multiline mode to match from start of line, preventing matches inside table rows
      // Matches: (entire line without pipes)(optional newline)(table header + separator)
      .replace(
        /(^[^\n|]*)(\n?)(\|[^\n]+\|\n\|[-:\s|]+\|)/gm,
        (match, lineBeforeTable, existingNewline, tableWithSeparator) => {
          // If line is empty and there was a newline, it's a blank line - don't modify
          if (lineBeforeTable === "" && existingNewline === "\n") {
            return match;
          }
          // Otherwise, ensure blank line before table
          return `${lineBeforeTable}\n\n${tableWithSeparator}`;
        },
      )
      // Fix missing newline before headers when directly after text/punctuation
      // But don't match if preceded by another # (to avoid breaking ### into # ##)
      .replace(/([^\n#])(\n?)(#{1,6}\s)/g, (match, char, newline, header) => {
        // If already has newline(s), keep original
        if (newline === "\n") return match;
        // If preceded by whitespace (but not newline), keep original
        if (/\s/.test(char)) return match;
        // Otherwise add double newline
        return `${char}\n\n${header}`;
      })
      // Reduce excessive newlines
      .replace(/\n{3,}/g, "\n\n")
  );
};

// Memoized ReactMarkdown for performance during streaming
const MemoizedReactMarkdown = memo(
  ReactMarkdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className,
);

// Markdown component overrides
const markdownComponents = {
  h1: ({ className, ...props }: ComponentProps<"h1">) => (
    <h1
      className={`mt-4 mb-2 text-2xl font-bold ${className || ""}`}
      {...props}
    />
  ),
  h2: ({ className, ...props }: ComponentProps<"h2">) => (
    <h2
      className={`mt-3 mb-2 text-xl font-bold ${className || ""}`}
      {...props}
    />
  ),
  h3: ({ className, ...props }: ComponentProps<"h3">) => (
    <h3
      className={`mt-3 mb-1 text-lg font-semibold ${className || ""}`}
      {...props}
    />
  ),
  p: ({ className, ...props }: ComponentProps<"p">) => (
    <p className={`mb-2 leading-7 ${className || ""}`} {...props} />
  ),
  a: ({ className, ...props }: ComponentProps<"a">) => (
    <a
      className={`underline ${className || ""}`}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  ul: ({ className, ...props }: ComponentProps<"ul">) => (
    <ul
      className={`mb-4 ml-6 list-disc [&>li]:mt-1 ${className || ""}`}
      {...props}
    />
  ),
  ol: ({ className, ...props }: ComponentProps<"ol">) => (
    <ol
      className={`mb-4 ml-6 list-decimal [&>li]:mt-1 ${className || ""}`}
      {...props}
    />
  ),
  code: ({ inline, className, children, ...props }: any) => {
    return inline ? (
      <code
        className={`px-1.5 py-0.5 rounded text-sm font-mono ${className || ""}`}
        {...props}
      >
        {children}
      </code>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ className, ...props }: ComponentProps<"pre">) => (
    <pre
      className={`mb-4 mt-2 overflow-x-auto rounded-lg p-4 ${className || ""}`}
      {...props}
    />
  ),
  table: ({ className, ...props }: ComponentProps<"table">) => (
    <div className="my-4 overflow-x-auto">
      <table
        className={`min-w-full border-collapse border ${className || ""}`}
        {...props}
      />
    </div>
  ),
  thead: ({ className, ...props }: ComponentProps<"thead">) => (
    <thead className={className || ""} {...props} />
  ),
  tbody: ({ className, ...props }: ComponentProps<"tbody">) => (
    <tbody className={className || ""} {...props} />
  ),
  tr: ({ className, ...props }: ComponentProps<"tr">) => (
    <tr className={className || ""} {...props} />
  ),
  th: ({ className, ...props }: ComponentProps<"th">) => (
    <th
      className={`px-3 py-2 text-left text-sm font-semibold border ${className || ""}`}
      {...props}
    />
  ),
  td: ({ className, ...props }: ComponentProps<"td">) => (
    <td className={`px-3 py-2 text-sm border ${className || ""}`} {...props} />
  ),
  blockquote: ({ className, ...props }: ComponentProps<"blockquote">) => (
    <blockquote
      className={`mt-4 border-l-4 pl-4 italic ${className || ""}`}
      {...props}
    />
  ),
};

export const SynthesizerMessage: React.FC<SynthesizerMessageProps> = ({
  message,
}) => {
  const { content, status } = message;

  const isProgress = status === "progress";
  const isError = status === "error";

  return (
    <div className="flex flex-col space-y-3">
      {/* Loading or error state */}
      {isProgress && (
        <div className="flex items-center gap-2 text-sm">
          <Spinner size="sm" variant="primary" />
          <span>Réponse en cours...</span>
        </div>
      )}
      {isError && (
        <div className="text-sm text-red-600">Erreur lors de la réponse</div>
      )}

      {/* Content */}
      {content && (
        <div>
          <ErrorBoundary
            fallback={<div className="whitespace-pre-wrap">{content}</div>}
          >
            <MemoizedReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight, rehypeRaw]}
              components={markdownComponents}
            >
              {fixMarkdown(content)}
            </MemoizedReactMarkdown>
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};
